import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { queryClient } from '../api/queryClient'

/**
 * Monitors JWT token validity. When the token expires:
 * - On tab visibility change (user comes back to idle tab)
 * - On periodic check (every 60s as fallback)
 *
 * Forces logout + redirect to login with an expired session message.
 */
export function useSessionMonitor() {
  const navigate = useNavigate()

  useEffect(() => {
    const checkToken = () => {
      const { token, isAuthenticated, hydrated } = useAuthStore.getState()
      if (!hydrated || !isAuthenticated) return

      if (!token) {
        // Token gone but still marked authenticated — force logout
        forceLogout()
        return
      }

      // Decode exp claim
      const parts = token.split('.')
      if (parts.length !== 3) {
        forceLogout()
        return
      }
      try {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
        if (!payload.exp || payload.exp * 1000 <= Date.now()) {
          forceLogout()
        }
      } catch {
        forceLogout()
      }
    }

    const forceLogout = () => {
      useAuthStore.getState().logout()
      queryClient.clear()
      navigate('/login?reason=expired', { replace: true })
    }

    // Check on visibility change (user switches back to the tab)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkToken()
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)

    // Periodic check every 60s as fallback
    const interval = setInterval(checkToken, 60_000)

    // Also check immediately on mount
    checkToken()

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      clearInterval(interval)
    }
  }, [navigate])
}